import axios from 'axios';
import { getConfig } from '../config';
import type { ProductOption } from '../models/Coupang';

export interface ConvertCpOptionsToWpRequest {
  brand: string;
  ssNameOnly: string;
  gpProductId: string;
  cpProductId: string;
  cpItemId: string;
  cpOptions: ProductOption[];
}

export interface RestructuredProduct {
  name?: string;
  cpNameKr: string;
  urlSlug: string;
  sku?: string;
  cpItemId?: string;
  cpItemName?: string;
  cpVendorItemId?: string;
  cpUrl: string;
  type: 'variable' | 'variation' | 'simple';
  attributeSize: string;
  attributePack: string;
  attributeColor: string;
  cpListPrice?: string;
  regularPrice?: string;
  brandSlug?: string;
  leafCategorySlug?: string;
  optionImageUrl?: string;
  featuredThumbnailUrl?: string;
}

export interface ConversionStats {
  cpOptionsCnt: number;
  variable: number;
  variation: number;
  simple: number;
  excluded: number;
}

export interface ConvertCpOptionsToWpResponse {
  cpProductId: string;
  products: RestructuredProduct[];
  stats: ConversionStats;
}

export interface CpOptionsToWpApiResponse {
  success: boolean;
  message?: string;
  data?: ConvertCpOptionsToWpResponse;
}

export interface RefreshCpOptionsRequest {
  cpProductId: string;
  cpItemId: string;
  cpVendorItemId: string;
}

export interface RefreshCpOptionsResponse {
  success: boolean;
  message?: string;
  data?: {
    cpProductId: string;
    cpItemId: string;
    cpVendorItemId: string;
    optionsCount: number;
    options: any[];
    refreshedAt: string;
    updated: boolean;
  };
}

export interface UpdateRestructuredProductsRequest {
  gpProductId: string;
  cpProductId: string;
  cpItemId: string;
  restructuredProducts: Array<Pick<RestructuredProduct, 'cpItemId' | 'featuredThumbnailUrl' | 'sku'>>;
}

export interface UpdateRestructuredProductsResponse {
  success: boolean;
  message?: string;
}

export class SourcingClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getConfig().apiUrl;
  }

  // Convert CP Options to WP Product Structure
  async convertCpOptionsToWp(requestData: ConvertCpOptionsToWpRequest): Promise<CpOptionsToWpApiResponse> {
    const url = `${this.baseUrl}/sourcing/cpOptionsToWp`;

    try {
      console.info('🔄 Converting CP options to WP structure:', requestData);

      const response = await axios.post(url, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.info('✅ CP options converted to WP structure successfully');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Failed to convert CP options to WP structure:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async refreshCpOptions(requestData: RefreshCpOptionsRequest): Promise<RefreshCpOptionsResponse> {
    const url = `${this.baseUrl}/sourcing/refreshCpOptions`;

    try {
      console.info('🔄 Refreshing CP options:', requestData);

      const response = await axios.post(url, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.info('✅ CP options refreshed successfully', response.data);
      return {
        success: true,
        message: response.data.message || 'CP options refreshed successfully',
        data: response.data,
      };
    } catch (error) {
      console.error('❌ Failed to refresh CP options:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Translate image from URL
  async translateImageFromUrl(
    imageUrl: string,
    targetLanguage = 'en',
  ): Promise<{
    success: boolean;
    translatedUrl?: string;
    message?: string;
  }> {
    const url = `${this.baseUrl}/sourcing/translateImage`;

    try {
      console.info('🌐 Translating image from URL:', imageUrl);

      const response = await axios.post(
        url,
        {
          imageUrl,
          targetLanguage,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      console.info('✅ Image translated successfully');
      return {
        success: true,
        translatedUrl: response.data.ohooImageUrl || response.data.translatedUrl || response.data.url,
      };
    } catch (error) {
      console.error('❌ Failed to translate image:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Translate image from base64 data
  async translateImageFromData(
    imageData: string,
    imageName: string,
    targetLanguage = 'en',
  ): Promise<{
    success: boolean;
    translatedUrl?: string;
    message?: string;
  }> {
    const url = `${this.baseUrl}/sourcing/translateImage`;

    try {
      console.info('🌐 Translating image from base64 data');

      const response = await axios.post(
        url,
        {
          imageData,
          targetLanguage,
          imageName,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      console.info('✅ Image translated successfully');
      return {
        success: true,
        translatedUrl: response.data.ohooImageUrl || response.data.translatedUrl || response.data.url,
      };
    } catch (error) {
      console.error('❌ Failed to translate image:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Update restructured products
  async updateRestructuredProducts(
    requestData: UpdateRestructuredProductsRequest,
  ): Promise<UpdateRestructuredProductsResponse> {
    const url = `${this.baseUrl}/sourcing/updateRestructuredProducts`;

    try {
      console.info('🔄 Updating restructured products:', requestData);

      const response = await axios.post(url, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.info('✅ Restructured products updated successfully');
      return {
        success: true,
        message: response.data.message || 'Restructured products updated successfully',
      };
    } catch (error) {
      console.error('❌ Failed to update restructured products:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// Export singleton instance
export const sourcingClient = new SourcingClient();
